/**
 * PHONE SHEETS THAT CANNOT LOSE THEIR BOTTOM ROW.
 *
 * Three things went wrong on a real iPhone and all three live here:
 *
 *  1. A sheet written as `fixed inset-0` is as tall as the LAYOUT viewport,
 *     which on iOS is the height with Safari's bottom toolbar hidden. The last
 *     50-90 px of the sheet therefore sits behind that toolbar, and the typing
 *     box and send button were in exactly that strip. `useViewportLock` ties
 *     the sheet to the viewport the shopper can actually see
 *     (`window.visualViewport`), so the toolbar — and the keyboard — take
 *     space away from the sheet instead of covering it.
 *  2. A sheet nested inside the list surface (`position: relative; z-index: 10`)
 *     is trapped in that stacking context, so a bar fixed at the bottom of the
 *     page painted and took taps ABOVE a sheet with a far higher z-index.
 *     `SheetLayer` renders the sheet into `document.body`, where its z-index
 *     means what it says.
 *  3. A phone reserves space at the bottom for the home indicator.
 *     `--yot-safe-bottom` (`env(safe-area-inset-bottom)`) is reserved by the
 *     `.yot-sheet-bottom` class in index.css.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/** Keep `--yot-vp` on the height of the viewport the shopper can see. */
export function useViewportLock() {
    useEffect(() => {
        const root = document.documentElement;
        const vv = window.visualViewport;
        const sync = () => {
            const h = Math.round(vv ? vv.height : window.innerHeight);
            root.style.setProperty('--yot-vp', `${h}px`);
            root.style.setProperty('--yot-vp-top', `${Math.round(vv?.offsetTop || 0)}px`);
        };
        sync();
        vv?.addEventListener('resize', sync);
        vv?.addEventListener('scroll', sync);
        window.addEventListener('resize', sync);
        window.addEventListener('orientationchange', sync);
        return () => {
            vv?.removeEventListener('resize', sync);
            vv?.removeEventListener('scroll', sync);
            window.removeEventListener('resize', sync);
            window.removeEventListener('orientationchange', sync);
            root.style.removeProperty('--yot-vp');
            root.style.removeProperty('--yot-vp-top');
        };
    }, []);
}

/** True while any full-screen grocery sheet is open. */
export function useAnySheetOpen() {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const sync = () => setOpen(!!document.querySelector('[data-grocery-sheet]'));
        const mo = new MutationObserver(sync);
        mo.observe(document.body, { childList: true, subtree: true });
        sync();
        return () => mo.disconnect();
    }, []);
    return open;
}

/** A sheet belongs to the screen, not to the panel that opened it. */
export function SheetLayer({ children }) {
    useViewportLock();
    if (typeof document === 'undefined') return children;
    return createPortal(children, document.body);
}
